import { KEYS, arrayToMap, randomId } from "@excalidraw/common";

import React from "react";

import {
  elementsAreInSameGroup,
  newElementWith,
  selectGroupsFromGivenElements,
} from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { LockedIcon, UnlockedIcon } from "../components/icons";
import { ToolButton } from "../components/ToolButton";

import { getSelectedElements } from "../scene";

import { t } from "../i18n";
import { getShortcutKey } from "../shortcut";

import {
  getBulkLockAvailability,
  getBulkLockTargetElements,
} from "../utils/lockUtils";

import { register } from "./register";

import type { AppState } from "../types";

const shouldLock = (elements: readonly ExcalidrawElement[]) =>
  elements.every((el) => !el.locked);

export const actionToggleElementLock = register({
  name: "toggleElementLock",
  label: (elements, appState, app) => {
    const selected = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: false,
    });

    return shouldLock(selected)
      ? "labels.elementLock.lock"
      : "labels.elementLock.unlock";
  },
  icon: (appState, elements) => {
    const selectedElements = getSelectedElements(elements, appState);
    return shouldLock(selectedElements) ? LockedIcon : UnlockedIcon;
  },
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements(appState);
    return (
      selectedElements.length > 0 &&
      !selectedElements.some((element) => element.locked && element.frameId)
    );
  },
  perform: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    if (!selectedElements.length) {
      return false;
    }

    const nextLockState = shouldLock(selectedElements);
    const selectedElementsMap = arrayToMap(selectedElements);

    const isAGroup =
      selectedElements.length > 1 && elementsAreInSameGroup(selectedElements);
    const isASingleUnit = selectedElements.length === 1 || isAGroup;
    const newGroupId = isASingleUnit ? null : randomId();

    let nextLockedMultiSelections = { ...appState.lockedMultiSelections };

    if (nextLockState) {
      nextLockedMultiSelections = {
        ...appState.lockedMultiSelections,
        ...(newGroupId ? { [newGroupId]: true } : {}),
      };
    } else if (isAGroup) {
      const groupId = selectedElements[0].groupIds.at(-1)!;
      delete nextLockedMultiSelections[groupId];
    }

    const nextElements = elements.map((element) => {
      if (!selectedElementsMap.has(element.id)) {
        return element;
      }

      let nextGroupIds = element.groupIds;

      // if locking together, add to group
      // if unlocking, remove the temporary group
      if (nextLockState) {
        if (newGroupId) {
          nextGroupIds = [...nextGroupIds, newGroupId];
        }
      } else {
        nextGroupIds = nextGroupIds.filter(
          (groupId) => !appState.lockedMultiSelections[groupId],
        );
      }

      return newElementWith(element, {
        locked: nextLockState,
        // do not recreate the array unncessarily
        groupIds:
          nextGroupIds.length !== element.groupIds.length
            ? nextGroupIds
            : element.groupIds,
      });
    });

    const nextElementsMap = arrayToMap(nextElements);
    const keepSelectionOnLock =
      nextLockState &&
      selectedElements.length === 1 &&
      selectedElements[0].type === "image";

    const nextSelectedElementIds: AppState["selectedElementIds"] =
      keepSelectionOnLock
        ? { [selectedElements[0].id]: true }
        : nextLockState
          ? {}
          : Object.fromEntries(selectedElements.map((el) => [el.id, true]));
    const unlockedSelectedElements = selectedElements.map(
      (el) => nextElementsMap.get(el.id) || el,
    );
    const nextSelectedGroupIds = keepSelectionOnLock
      ? {}
      : nextLockState
        ? {}
        : selectGroupsFromGivenElements(unlockedSelectedElements, appState);

    const activeLockedId = nextLockState
      ? newGroupId
        ? newGroupId
        : isAGroup
          ? selectedElements[0].groupIds.at(-1)!
          : selectedElements[0].id
      : null;

    // Collaboration broadcast removed - not implemented in current version

    return {
      elements: nextElements,

      appState: {
        ...appState,
        selectedElementIds: nextSelectedElementIds,
        selectedGroupIds: nextSelectedGroupIds,
        selectedLinearElement: nextLockState
          ? null
          : appState.selectedLinearElement,
        lockedMultiSelections: nextLockedMultiSelections,
        activeLockedId,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) => {
    return (
      event.key.toLocaleLowerCase() === KEYS.L &&
      event[KEYS.CTRL_OR_CMD] &&
      event.shiftKey &&
      app.scene.getSelectedElements({
        selectedElementIds: appState.selectedElementIds,
        includeBoundTextElement: false,
      }).length > 0
    );
  },
  PanelComponent: ({ appState, updateData, app }) => {
    const selected = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: false,
    });

    const nextIsLock = shouldLock(selected);
    const labelKey = nextIsLock
      ? "labels.elementLock.lock"
      : "labels.elementLock.unlock";
    const Icon = nextIsLock ? LockedIcon : UnlockedIcon;

    const disabled =
      selected.length === 0 ||
      selected.some((element) => element.locked && element.frameId);

    return React.createElement(ToolButton, {
      type: "button",
      icon: Icon,
      title: `${t(labelKey)} — ${getShortcutKey("CtrlOrCmd+Shift+L")}`,
      "aria-label": t(labelKey),
      onClick: () => updateData(null),
      disabled,
    });
  },
});

export const actionUnlockAllElements = register({
  name: "unlockAllElements",
  trackEvent: { category: "canvas" },
  viewMode: false,
  icon: UnlockedIcon,
  predicate: (elements, appState) => {
    const selectedElements = getSelectedElements(elements, appState);
    return (
      selectedElements.length === 0 &&
      elements.some((element) => element.locked)
    );
  },
  perform: (elements, appState, _, app) => {
    const lockedElements = elements.filter((el) => el.locked);

    const nextElements = elements.map((element) => {
      if (element.locked) {
        // remove the temporary groupId if it exists
        const nextGroupIds = element.groupIds.filter(
          (gid) => !appState.lockedMultiSelections[gid],
        );

        return newElementWith(element, {
          locked: false,
          groupIds:
            // do not recreate the array unncessarily
            element.groupIds.length !== nextGroupIds.length
              ? nextGroupIds
              : element.groupIds,
        });
      }
      return element;
    });

    const nextElementsMap = arrayToMap(nextElements);

    const unlockedElements = lockedElements.map(
      (el) => nextElementsMap.get(el.id) || el,
    );

    // Collaboration broadcast removed - not implemented in current version

    return {
      elements: nextElements,
      appState: {
        ...appState,
        selectedElementIds: Object.fromEntries(
          lockedElements.map((el) => [el.id, true]),
        ),
        selectedGroupIds: selectGroupsFromGivenElements(
          unlockedElements,
          appState,
        ),
        lockedMultiSelections: {},
        activeLockedId: null,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  label: "labels.elementLock.unlockAll",
});

export const actionLockAllElements = register({
  name: "lockAllElements",
  trackEvent: { category: "canvas" },
  viewMode: false,
  icon: LockedIcon,
  predicate: (elements, appState) => {
    const selectedElements = getSelectedElements(elements, appState);
    const availability = getBulkLockAvailability(elements, appState);

    return selectedElements.length === 0 && availability.canLockAll;
  },
  perform: (elements, appState, _, app) => {
    const targetElements = getBulkLockTargetElements(
      elements,
      appState,
      "lock",
    );

    if (targetElements.length === 0) {
      return false;
    }

    const targetElementsMap = arrayToMap(targetElements);
    const newGroupId = targetElements.length > 1 ? randomId() : null;

    let nextLockedMultiSelections = { ...appState.lockedMultiSelections };
    if (newGroupId) {
      nextLockedMultiSelections = {
        ...appState.lockedMultiSelections,
        [newGroupId]: true,
      };
    }

    const nextElements = elements.map((element) => {
      if (!targetElementsMap.has(element.id)) {
        return element;
      }

      let nextGroupIds = element.groupIds;
      if (newGroupId) {
        nextGroupIds = [...nextGroupIds, newGroupId];
      }

      return newElementWith(element, {
        locked: true,
        groupIds:
          nextGroupIds.length !== element.groupIds.length
            ? nextGroupIds
            : element.groupIds,
      });
    });

    const activeLockedId =
      newGroupId || (targetElements.length === 1 ? targetElements[0].id : null);

    // Broadcast lock state change to collaborators
    if (app.props.isCollaborating) {
      const elementIds = targetElements.map((el) => el.id);
      // Import the collaboration API dynamically to avoid circular dependencies
      import("../../../excalidraw-app/app-jotai").then(({ appJotaiStore }) => {
        import("../../../excalidraw-app/collab/Collab").then(
          ({ collabAPIAtom }) => {
            const collabAPI = appJotaiStore.get(collabAPIAtom);
            if (collabAPI?.isCollaborating()) {
              collabAPI.broadcastLockStateChange(elementIds, true);
            }
          },
        );
      });
    }

    return {
      elements: nextElements,
      appState: {
        ...appState,
        selectedElementIds: {},
        selectedGroupIds: {},
        selectedLinearElement: null,
        lockedMultiSelections: nextLockedMultiSelections,
        activeLockedId,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  label: "labels.elementLock.lockAll",
  keyTest: (event, appState, elements) => {
    return (
      event.key.toLocaleLowerCase() === KEYS.L &&
      event[KEYS.CTRL_OR_CMD] &&
      event.altKey &&
      !event.shiftKey &&
      getSelectedElements(elements, appState).length === 0
    );
  },
});

export const actionLockSelectedElements = register({
  name: "lockSelectedElements",
  trackEvent: { category: "element" },
  viewMode: false,
  icon: LockedIcon,
  predicate: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements(appState);
    return (
      selectedElements.length > 0 &&
      selectedElements.some((el) => !el.locked) &&
      !selectedElements.some((element) => element.locked && element.frameId)
    );
  },
  perform: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    const targetElements = selectedElements.filter((el) => !el.locked);

    if (targetElements.length === 0) {
      return false;
    }

    const targetElementsMap = arrayToMap(targetElements);
    const newGroupId = targetElements.length > 1 ? randomId() : null;

    let nextLockedMultiSelections = { ...appState.lockedMultiSelections };
    if (newGroupId) {
      nextLockedMultiSelections = {
        ...appState.lockedMultiSelections,
        [newGroupId]: true,
      };
    }

    const nextElements = elements.map((element) => {
      if (!targetElementsMap.has(element.id)) {
        return element;
      }

      let nextGroupIds = element.groupIds;
      if (newGroupId) {
        nextGroupIds = [...nextGroupIds, newGroupId];
      }

      return newElementWith(element, {
        locked: true,
        groupIds:
          nextGroupIds.length !== element.groupIds.length
            ? nextGroupIds
            : element.groupIds,
      });
    });

    const activeLockedId =
      newGroupId || (targetElements.length === 1 ? targetElements[0].id : null);

    // Collaboration broadcast removed - not implemented in current version

    return {
      elements: nextElements,
      appState: {
        ...appState,
        selectedElementIds: {},
        selectedGroupIds: {},
        selectedLinearElement: null,
        lockedMultiSelections: nextLockedMultiSelections,
        activeLockedId,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  label: "labels.elementLock.lockSelected",
  keyTest: (event, appState, elements, app) => {
    return (
      event.key.toLocaleLowerCase() === KEYS.L &&
      event[KEYS.CTRL_OR_CMD] &&
      event.shiftKey &&
      app.scene.getSelectedElements({
        selectedElementIds: appState.selectedElementIds,
        includeBoundTextElement: false,
      }).length > 0
    );
  },
});

export const actionUnlockSelectedElements = register({
  name: "unlockSelectedElements",
  trackEvent: { category: "element" },
  viewMode: false,
  icon: UnlockedIcon,
  predicate: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements(appState);
    return (
      selectedElements.length > 0 && selectedElements.some((el) => el.locked)
    );
  },
  perform: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    const targetElements = selectedElements.filter((el) => el.locked);

    if (targetElements.length === 0) {
      return false;
    }

    const targetElementsMap = arrayToMap(targetElements);

    const nextElements = elements.map((element) => {
      if (!targetElementsMap.has(element.id)) {
        return element;
      }

      // remove the temporary groupId if it exists
      const nextGroupIds = element.groupIds.filter(
        (gid) => !appState.lockedMultiSelections[gid],
      );

      return newElementWith(element, {
        locked: false,
        groupIds:
          element.groupIds.length !== nextGroupIds.length
            ? nextGroupIds
            : element.groupIds,
      });
    });

    const nextElementsMap = arrayToMap(nextElements);
    const unlockedElements = targetElements.map(
      (el) => nextElementsMap.get(el.id) || el,
    );

    // Collaboration broadcast removed - not implemented in current version

    return {
      elements: nextElements,
      appState: {
        ...appState,
        selectedElementIds: Object.fromEntries(
          targetElements.map((el) => [el.id, true]),
        ),
        selectedGroupIds: selectGroupsFromGivenElements(
          unlockedElements,
          appState,
        ),
        lockedMultiSelections: Object.fromEntries(
          Object.entries(appState.lockedMultiSelections).filter(
            ([groupId]) =>
              !targetElements.some((el) => el.groupIds.includes(groupId)),
          ),
        ),
        activeLockedId: null,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  label: "labels.elementLock.unlockSelected",
  keyTest: (event, appState, elements, app) => {
    return (
      event.key.toLocaleLowerCase() === KEYS.U &&
      event[KEYS.CTRL_OR_CMD] &&
      event.shiftKey &&
      app.scene.getSelectedElements({
        selectedElementIds: appState.selectedElementIds,
        includeBoundTextElement: false,
      }).length > 0
    );
  },
});
